import { getFromLocalStorage, makeId, setLocalStorage } from './utils';

async function getLocalInformation(): Promise<Map<string, string> | undefined> {
    const response = await fetch('https://www.cloudflare.com/cdn-cgi/trace');

    if (!response.ok) {
        return undefined;
    }

    const result = new Map<string, string>();
    const text = await response.text();

    for (const item of text.split('\n')) {
        const [key, value] = item.split('=');

        result.set(key, value);
    }

    return result;
}

export async function getDeviceIp(){
    // Attempt to retrieve previously stored device IP, otherwise attempt to fetch it
    const deviceIp = getFromLocalStorage('deviceIp');
    if (deviceIp === null){
        try{
            await getDeviceName();
        } catch(error) { }
    }

    return getFromLocalStorage('deviceIp');
}

export async function getDeviceGeolocation(){
    // API-based IP Geocoding
    const deviceLocationString = getFromLocalStorage('deviceLocationString');
    if(deviceLocationString !== null && deviceLocationString !== 'undefined, undefined, undefined'){
        return deviceLocationString;
    }
    const deviceIp = await getDeviceIp();

    if (deviceIp === 'IP UNKNOWN' || !deviceIp){
        return;
    }

    // Note IPAPI.co free tier has 1k/day limit
    // Consider upgrading to paid tier if we start throttling
    const ipApiResponse = await fetch(`https://ipapi.co/${deviceIp}/json`);

    if(!ipApiResponse.ok){
        console.warn('IP Geolocation failure');
        return;
    }

    const ipApiJSON = await ipApiResponse.json();
    const locationString = `${String(ipApiJSON.city)}, ${String(ipApiJSON.region)}, ${String(ipApiJSON.country_name)}`;
    setLocalStorage('deviceLocationString', locationString);

    return locationString;
}

export async function getDeviceName() {
    let deviceName = getFromLocalStorage('deviceName');
    const deviceIp = getFromLocalStorage('deviceIp');
    if (deviceName === null || deviceIp === null) {
        let localInformation;
        try {
            localInformation = await getLocalInformation();
        } catch (error) {
            // Handle the error here, if necessary
            // Set localInformation to null or undefined to keep the function moving forward
            localInformation = null;
        }

        // ReplaceAll must not be deleted - Safari provides IP addresses with colons rather than periods which interferes with our parsing logic.
        deviceName = `${makeId()} (${
            localInformation?.get('ip')?.replaceAll(':', '.') ?? 'IP UNKNOWN'
        }, ${localInformation?.get('colo') ?? 'COLO UNKNOWN'}, ${localInformation?.get('loc') ?? 'LOC UNKNOWN'})`;

        setLocalStorage('deviceName', deviceName);
        setLocalStorage('deviceIp', localInformation?.get('ip')?.replaceAll(':', '.') ?? 'IP UNKNNOWN');
    }

    return deviceName;
}

export function inIframe() {
    return window.location !== window.parent.location;
}

/** Hostname of the page embedding us, when we're in an iframe and the browser shares a referrer. */
export function embedHost(): string | undefined {
    if (!inIframe() || document.referrer === '') {
        return undefined;
    }
    try {
        return new URL(document.referrer).hostname.toLowerCase();
    } catch {
        return undefined;
    }
}

const IN_APP_WEBVIEW_REGEX = /Instagram|FBAN|FBAV|FB_IAB|Snapchat|TikTok|musical_ly|BytedanceWebview|Line\/|Pinterest|LinkedInApp/i;

// In-app browsers (Instagram, Facebook, etc.) typically use isolated, ephemeral storage,
// so a saved sign-in often won't survive to the next visit.
export function isInAppWebview(): boolean {
    return IN_APP_WEBVIEW_REGEX.test(navigator.userAgent);
}

// True when storage clearly can't persist the sign-in right now (blocked cookies / localStorage).
// Note: this cannot detect Safari ITP eviction, which passes this check but wipes storage days later.
export function storageBlocked(): boolean {
    try {
        const key = '__proxi_storage_test__';
        localStorage.setItem(key, '1');
        const ok = localStorage.getItem(key) === '1';
        localStorage.removeItem(key);
        if (!ok) {
            return true;
        }
    } catch {
        return true;
    }
    return !navigator.cookieEnabled;
}

export function inSmallScreen() {
    return Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0) <= 550;
}

export function detectMobileViewport(previewMode?: boolean | undefined) {
    if (previewMode === true) {
        return true;
    }

    const bodySelector = document.querySelector('body');

    if (bodySelector) {
        return bodySelector.clientWidth < 551;
    }

    return false;
}

export function getCookie(cname: string) {
    const name = cname + '=';
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return undefined;
}